import { ASSET_TYPES, BIZ_TYPES } from "../helpers/assetOptions";
import { DTO_FIELDS } from "../helpers/dtoWhitelist";
import { toDateTime, riskLevelByDate } from "./hardwareRecord";

/**
 * 軟體 mock store 的一筆 = **後端 DTO 形狀**（票 poc-20260922/07；與 hardwareRecord 同一套做法）。
 *
 * 存 `DcSoftwareDto` 的可寫欄位，加上 `DcSoftwareListItemDto` 才有、又推導不出來的 CVE／KEV 等級與命中數。
 * 後端算得出來的回傳時才算：EOS／EOL 風險等級、管理者（負責關係）、類別名稱（主檔）、部署設備（關聯＋硬體）。
 * 後端沒有的（弱掃／滲透明細與上傳檔、IP 分頁、審核單號、異動歷程、部署關聯本身）收在 `mock_only`。
 */

/** 營運環境:Common/OperatingEnvironments 的 id(1–4);「DMZ區域」等主檔外的值只留名稱、id 0 */
export const ENV_IDS = { 營運: 1, 測試: 2, 開發: 3, 備援: 4 };

const SAME_VALUE = [
  ["code", "asset_no"], ["name", "host_name"], ["serialNo", "software_no"], ["version", "version"],
  ["brand", "brand"], ["brandId", "brand_id"], ["model", "model"], ["modelId", "model_id"],
  ["propertyNo", "property_no"], ["remark", "remark"], ["vendorName", "vendor_name"],
  ["purchaseAmount", "purchase_amount"], ["currency", "currency_code"],
  ["latestVersion", "latest_version"], ["cpe", "cpe"],
  ["cveLevel", "cve_risk_level"], ["kevLevel", "kev_risk_level"],
  ["cveCount", "cve_count"], ["kevCount", "kev_count"],
  ["vulnerabilityHits", "vulnerability_hits"],
  ["createdOn", "created_on"], ["updatedOn", "updated_on"],
];
const DATES = [
  ["eos", "eos_date"], ["eol", "eol_date"], ["endOfSale", "end_of_sale_date"],
  ["cveDate", "cve_date"], ["kevDate", "kev_date"], ["deployedDate", "deployed_date"],
];
const GROUPS = [
  // 前端群組名 → [日期鍵, 下次日期鍵, 檔案 URL 鍵]
  ["scan", "vulnerability_scan_date", "next_vulnerability_scan_date", "vulnerability_scan_file_url"],
  ["pentest", "penetration_test_date", "next_penetration_test_date", "penetration_test_file_url"],
];
const DERIVED = new Set([
  "id", "manager", "managerId", "keyBiz", "bizTypeId", "assetTypeId", "envId", "cveHits", "deployments",
]);
const HANDLED = new Set([
  ...SAME_VALUE.map(([k]) => k), ...DATES.map(([k]) => k), ...GROUPS.map(([k]) => k),
  "starred", "status", "bizType", "assetType", "env",
]);

const dateOnly = (v) => (typeof v === "string" ? v.slice(0, 10) : v || "");
const nameById = (list, id) => (Number(id) > 0 ? list[Number(id) - 1] || "" : "");
const idByName = (list, name) => list.indexOf(name) + 1 || 0;

/** stored(DTO)→ 舊消費者的形狀 */
export function softwareToLegacy(row) {
  if (!row) return row;
  const m = row.mock_only || {};
  const out = { ...m, id: row.id };
  SAME_VALUE.forEach(([k, d]) => { if (row[d] !== undefined) out[k] = row[d]; });
  DATES.forEach(([k, d]) => { if (row[d] !== undefined) out[k] = dateOnly(row[d]); });
  GROUPS.forEach(([g, dateKey, nextKey]) => {
    out[g] = {
      date: dateOnly(row[dateKey]), nextDate: dateOnly(row[nextKey]),
      files: m[`${g}Files`] || [], rows: m[`${g}Rows`] || [],
    };
    delete out[`${g}Files`];
    delete out[`${g}Rows`];
  });
  out.starred = !!row.is_key_business;
  out.status = m.status || (row.active === false ? "停用" : "使用中");
  out.bizType = nameById(BIZ_TYPES, row.business_category_id);
  out.assetType = nameById(ASSET_TYPES, row.asset_category_id);
  out.env = row.environment_name || "";
  out.deployedHardwareIds = m.deployedHardwareIds || [];
  return out;
}

/** 舊消費者寫進來的欄位 → 新的 stored row */
export function softwareFromLegacy(fields, prev = {}) {
  const next = { ...prev };
  const m = { ...(prev.mock_only || {}) };
  SAME_VALUE.forEach(([k, d]) => { if (fields[k] !== undefined) next[d] = fields[k]; });
  DATES.forEach(([k, d]) => { if (fields[k] !== undefined) next[d] = toDateTime(fields[k]); });
  GROUPS.forEach(([g, dateKey, nextKey]) => {
    if (fields[g] === undefined) return;
    const grp = fields[g] || {};
    next[dateKey] = toDateTime(grp.date);
    next[nextKey] = toDateTime(grp.nextDate);
    m[`${g}Files`] = grp.files || [];
    m[`${g}Rows`] = grp.rows || [];
  });
  if (fields.starred !== undefined) next.is_key_business = !!fields.starred;
  if (fields.status !== undefined) {
    next.active = fields.status !== "停用";
    if (fields.status === "閒置") m.status = "閒置"; else delete m.status;
  }
  if (fields.bizType !== undefined) next.business_category_id = idByName(BIZ_TYPES, fields.bizType);
  if (fields.assetType !== undefined) next.asset_category_id = idByName(ASSET_TYPES, fields.assetType);
  if (fields.env !== undefined) {
    next.environment_name = fields.env || "";
    next.environment_id = ENV_IDS[fields.env] || 0;
  }
  Object.keys(fields).forEach((k) => {
    if (HANDLED.has(k) || DERIVED.has(k) || k === "mock_only") return;
    if (/^[a-z]+(_[a-z0-9]+)+$/.test(k)) return; // DTO 鍵走 raw 寫入
    m[k] = fields[k];
  });
  next.mock_only = m;
  return next;
}

export const softwareCodec = { toView: softwareToLegacy, toStore: softwareFromLegacy };

const READ_ONLY = new Set([
  "id", "created_on", "updated_on", "business_category_name", "asset_category_name",
  "manager_customer_name", "vulnerability_hits", "cve_date", "kev_date", "latest_version",
]);
export const SOFTWARE_WRITABLE = DTO_FIELDS.DcSoftwareDto.filter((k) => !READ_ONLY.has(k));
const DATE_KEYS = new Set([
  "deployed_date", "eos_date", "eol_date", "end_of_sale_date",
  "vulnerability_scan_date", "next_vulnerability_scan_date", "penetration_test_date", "next_penetration_test_date",
]);

/** payload(DcSoftwareDto)→ 存進 store 的欄位 */
export function softwareFieldsFromDto(dto) {
  const out = {};
  SOFTWARE_WRITABLE.forEach((k) => {
    if (dto[k] === undefined) return;
    out[k] = DATE_KEYS.has(k) ? toDateTime(dto[k]) : dto[k];
  });
  // 名稱是 id 的顯示鏡像:只送 id 時照主檔補上
  if (out.environment_id !== undefined && out.environment_name === undefined) {
    out.environment_name = Object.keys(ENV_IDS).find((k) => ENV_IDS[k] === Number(out.environment_id)) || "";
  }
  return out;
}

export const SOFTWARE_LIST_ITEM_KEYS = [
  "asset_no", "software_no", "host_name", "is_key_business", "brand", "model", "version",
  "environment_name", "deployment_count", "eos_date", "eos_risk_level", "eol_date", "eol_risk_level",
  "cve_date", "cve_risk_level", "cve_count", "kev_date", "kev_risk_level", "kev_count",
  "manager_customer_id", "manager_customer_name", "is_under_review", "approval_request_no", "deployments", "id",
];

/**
 * `ctx`:
 *   manager(id)        → { managerId, manager }（負責關係）
 *   deployments(ids)   → DcSoftwareDeploymentItemDto[]（由硬體算）
 */
export function softwareListItemDto(row, ctx) {
  const m = row.mock_only || {};
  const mgr = ctx.manager(row.id);
  const deployments = ctx.deployments(m.deployedHardwareIds || []);
  const pending = m.approvalStatus === "pending";
  return {
    asset_no: row.asset_no || "",
    software_no: row.software_no || "",
    host_name: row.host_name || "",
    is_key_business: !!row.is_key_business,
    brand: row.brand || "",
    model: row.model || "",
    version: row.version || "",
    environment_name: row.environment_name || "",
    deployment_count: deployments.length,
    eos_date: row.eos_date || null,
    eos_risk_level: riskLevelByDate(row.eos_date),
    eol_date: row.eol_date || null,
    eol_risk_level: riskLevelByDate(row.eol_date),
    cve_date: row.cve_date || null,
    cve_risk_level: row.cve_risk_level || null,
    cve_count: Number(row.cve_count) || 0,
    kev_date: row.kev_date || null,
    kev_risk_level: row.kev_risk_level || null,
    kev_count: Number(row.kev_count) || 0,
    manager_customer_id: Number(mgr.managerId) || 0,
    manager_customer_name: mgr.manager || "",
    is_under_review: pending,
    approval_request_no: pending ? m.docNo || "" : "",
    deployments,
    id: row.id,
  };
}

/** 編輯頁 DTO:鍵集合＝swagger DcSoftwareDto */
export function softwareDetailDto(row, ctx) {
  const mgr = ctx.manager(row.id);
  const derived = {
    business_category_name: nameById(BIZ_TYPES, row.business_category_id),
    asset_category_name: nameById(ASSET_TYPES, row.asset_category_id),
    manager_customer_id: Number(mgr.managerId) || 0,
    manager_customer_name: mgr.manager || "",
  };
  const DEFAULTS = {
    business_category_id: 0, is_key_business: false, property_no: "", host_name: "",
    asset_category_id: 0, asset_no: "", brand_id: 0, brand: "", model_id: 0, model: "",
    software_no: "", version: "", environment_id: 0, environment_name: "", remark: "", vendor_name: "",
    deployed_date: null, purchase_amount: 0, currency_code: "TWD", active: true,
    created_on: "2026-08-06T23:36:47+08:00", updated_on: "2026-08-30T18:19:08+08:00",
    vulnerability_hits: [],
  };
  const out = {};
  DTO_FIELDS.DcSoftwareDto.forEach((k) => {
    if (k in derived) out[k] = derived[k];
    else if (row[k] !== undefined && row[k] !== null) out[k] = row[k];
    else out[k] = k in DEFAULTS ? DEFAULTS[k] : null;
  });
  out.id = row.id;
  return out;
}
